/**
 * groupTimelineItems — pure render-level derivation that classifies reasoning by
 * position and groups tool calls into expandable summaries (design 2026-06-16,
 * building on docs/features/agent-case-discovery-v3/plans/2026-06-11-tool-group-summary-ui.html).
 *
 * Two kinds of reasoning:
 *  - a `thinking` block emitted IMMEDIATELY AFTER a tool (i.e. while a tool run is
 *    open) is reasoning ABOUT those tool results → it joins the tool group as a
 *    member (rendered like a tool line in the group's expanded timeline);
 *  - any other `thinking` (initial planning, or after a text message) → hoisted
 *    into a single `thinking_group` rendered at the top.
 *
 * The flat `TimelineItem[]` stays the single source of truth — the reducer,
 * the WS protocol and history restoration are untouched. Grouping happens only
 * at render time.
 *
 * Mirror: apps/addin-word hector-agent-edition-v3 has its own copy of this
 * logic over `ChatElement[]` — keep grouping rules in sync.
 */

import type { TimelineItem } from '../types/session.types';

/** A timeline item narrowed to the `tool` variant. */
export type ToolTimelineItem = Extract<TimelineItem, { type: 'tool' }>;

/** A timeline item narrowed to the `thinking` variant. */
export type ThinkingTimelineItem = Extract<TimelineItem, { type: 'thinking' }>;

/**
 * A member of a tool group: a tool call, or a reasoning block that immediately
 * followed a tool (and is therefore tied to that run). Members keep their
 * chronological order inside the group.
 */
export type ToolGroupMember = ToolTimelineItem | ThinkingTimelineItem;

/**
 * One entry of the render list:
 *  - `thinking_group`: standalone reasoning (not tied to tools), hoisted to the top;
 *  - `tool_group`: a run of consecutive tools + the reasoning blocks that followed them;
 *  - `item`: any other timeline item rendered as today.
 */
export type TimelineDisplayItem =
  | { kind: 'item'; item: TimelineItem }
  | { kind: 'tool_group'; id: string; members: ToolGroupMember[] }
  | { kind: 'thinking_group'; id: string; thinkings: ThinkingTimelineItem[] };

/**
 * `context_compaction` is emitted as a `tool` event but is rendered as a
 * full-width separator («Historique de la conversation compressé») — it must
 * never be absorbed into a group, and it breaks the current run.
 */
function isGroupableTool(item: TimelineItem): item is ToolTimelineItem {
  return item.type === 'tool' && item.event.tool !== 'context_compaction';
}

/**
 * Derives the render list from the flat timeline (design 2026-06-16):
 *
 *  - groupable `tool` items accumulate into a `tool_group` run;
 *  - a `thinking` that lands while a run is open joins that run (reasoning about
 *    the tool results) — it does NOT break the run, so `tool, thinking, tool`
 *    stays ONE group `[tool, thinking, tool]`;
 *  - a `thinking` with no open run (initial, or after a message/JP/etc.) is
 *    collected into the top `thinking_group`;
 *  - any non-tool, non-thinking item (content, JP, case wiki, error,
 *    context_compaction) breaks the run and passes through unchanged.
 *
 * Group ids are the id of the FIRST member they contain (stable across streaming
 * deltas — derived from the backend `invocation_id` in the reducer). Used as the
 * React key: an unstable id would remount the component on every delta and lose
 * the user's expanded state.
 *
 * The CD timeline holds a single exchange (no user_message items — those wrap it
 * upstream), so the thinking_group sits at the top of that one response. The
 * add-in mirror scopes the hoist per turn because its timeline is multi-turn.
 */
export function groupTimelineItems(timeline: TimelineItem[]): TimelineDisplayItem[] {
  const result: TimelineDisplayItem[] = [];
  const topThinkings: ThinkingTimelineItem[] = [];
  let currentRun: ToolGroupMember[] | null = null;

  const flushRun = () => {
    if (currentRun) {
      result.push({ kind: 'tool_group', id: currentRun[0]!.id, members: currentRun });
      currentRun = null;
    }
  };

  for (const item of timeline) {
    if (isGroupableTool(item)) {
      if (currentRun) currentRun.push(item);
      else currentRun = [item];
      continue;
    }
    if (item.type === 'thinking') {
      // Reasoning right after a tool (open run) belongs to that run; otherwise
      // it's standalone reasoning hoisted to the top.
      if (currentRun) currentRun.push(item);
      else topThinkings.push(item);
      continue;
    }
    flushRun();
    result.push({ kind: 'item', item });
  }
  flushRun();

  // Prepend the standalone reasoning as a single group at the very top.
  if (topThinkings.length > 0) {
    result.unshift({ kind: 'thinking_group', id: topThinkings[0]!.id, thinkings: topThinkings });
  }

  return result;
}

/**
 * One node of the expanded group timeline: ordinary tools render as one line
 * each; consecutive `invoke_subagent` calls collapse into a single node
 * («A exécuté N sous-agents») displayed as a card grid; a reasoning member
 * renders as its own line.
 */
export type ToolGroupNode =
  | { kind: 'tool'; item: ToolTimelineItem }
  | { kind: 'subagent_run'; id: string; items: ToolTimelineItem[] }
  | { kind: 'thinking'; item: ThinkingTimelineItem };

/**
 * Splits a group's members into expanded-view nodes. An ordinary tool (or a
 * reasoning block) between two subagents splits the subagent run in two
 * (chronological order is preserved — the LLM call order is the contract).
 */
export function splitToolGroupIntoNodes(members: ToolGroupMember[]): ToolGroupNode[] {
  const nodes: ToolGroupNode[] = [];
  let subagentRun: ToolTimelineItem[] | null = null;

  const flushSubagents = () => {
    if (subagentRun) {
      nodes.push({ kind: 'subagent_run', id: subagentRun[0]!.id, items: subagentRun });
      subagentRun = null;
    }
  };

  for (const member of members) {
    if (member.type === 'thinking') {
      flushSubagents();
      nodes.push({ kind: 'thinking', item: member });
      continue;
    }
    if (member.event.tool === 'invoke_subagent') {
      if (subagentRun) subagentRun.push(member);
      else subagentRun = [member];
      continue;
    }
    flushSubagents();
    nodes.push({ kind: 'tool', item: member });
  }

  flushSubagents();

  return nodes;
}

/**
 * Subagents are delegated AI runs, not ordinary backend plumbing. The collapsed
 * tool group keeps every subagent visible, even after terminal status, so the
 * lawyer can see which delegated runs are still spinning and which finished
 * with a check, error, or cancellation.
 */
export function getSubagentPreviewItems(members: ToolGroupMember[]): ToolTimelineItem[] {
  return members.filter(
    (member): member is ToolTimelineItem =>
      member.type === 'tool' &&
      member.event.tool === 'invoke_subagent',
  );
}

export interface CollapsedSubagentPreview {
  visible: ToolTimelineItem[];
  hiddenCount: number;
  hiddenStatusSummary: string | null;
}

const DEFAULT_COLLAPSED_SUBAGENT_LIMIT = 4;

function summarizeSubagentStatuses(items: ToolTimelineItem[]): string | null {
  const completedCount = items.filter(item => item.event.status === 'completed').length;
  const runningCount = items.filter(item => item.event.status === 'started' || item.event.status === 'in_progress').length;
  const errorCount = items.filter(item => item.event.status === 'error').length;
  const parts: string[] = [];

  if (completedCount > 0) parts.push(`${completedCount} terminé${completedCount > 1 ? 's' : ''}`);
  if (runningCount > 0) parts.push(`${runningCount} en cours`);
  if (errorCount > 0) parts.push(`${errorCount} erreur${errorCount > 1 ? 's' : ''}`);

  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Keeps the collapsed subagent preview readable when the orchestrator launches
 * many delegated runs. The expanded timeline still renders every subagent.
 */
export function getCollapsedSubagentPreview(
  items: ToolTimelineItem[],
  limit = DEFAULT_COLLAPSED_SUBAGENT_LIMIT,
): CollapsedSubagentPreview {
  if (items.length <= limit) {
    return { visible: items, hiddenCount: 0, hiddenStatusSummary: null };
  }

  const hidden = items.slice(limit);
  return {
    visible: items.slice(0, limit),
    hiddenCount: hidden.length,
    hiddenStatusSummary: summarizeSubagentStatuses(hidden),
  };
}
